//! Pagina BI: landing per l'integrazione Metabase.
//!
//! Non è una dashboard live: è una pagina informativa che indirizza l'operatore
//! verso l'istanza Metabase esterna (porta 3001 in dev). Spiega:
//!   1. come fare il primo setup (account admin + add database read-only);
//!   2. quali dashboard suggerite costruire dalle 6 views BI pre-esposte;
//!   3. le credenziali del DB read-only (copia rapida) — solo per ambienti dev.
//!
//! In produzione le credenziali verranno gestite via secrets manager (Vault /
//! KMS) e non saranno più hard-coded qui. Per ora restano per accelerare
//! l'onboarding del team operations.

import { useState } from "react";

const METABASE_URL = "http://localhost:3002";

interface BiDashboard {
  title: string;
  description: string;
  view: string;
}

const DASHBOARDS: BiDashboard[] = [
  {
    title: "Markets Overview",
    description:
      "Markets aperti per categoria, volume aggregato, hours-to-resolve countdown.",
    view: "quantica_bi.bi_markets_overview",
  },
  {
    title: "Volume Trading 7d",
    description:
      "Trade count + volume USD giorno per giorno, last 7 days.",
    view: "quantica_bi.bi_volume_7d",
  },
  {
    title: "KYC Tier Distribution",
    description:
      "Utenti per tier + paese, new users last 30d, 2FA adoption.",
    view: "quantica_bi.bi_users_kyc_distribution",
  },
  {
    title: "Withdrawal Queue",
    description:
      "Withdrawals per status/giorno/asset, fees aggregate.",
    view: "quantica_bi.bi_withdrawals_queue",
  },
  {
    title: "Oracle Resolution Audit",
    description:
      "Resolution attempts per source + status, success rate, avg time-to-finalize.",
    view: "quantica_bi.bi_resolution_attempts",
  },
  {
    title: "Treasury Snapshot",
    description:
      "Bond UMA lockati, pending rewards, lifetime forfeited.",
    view: "quantica_bi.bi_treasury_snapshot",
  },
];

interface DbCredentials {
  host: string;
  port: string;
  database: string;
  schema: string;
  user: string;
  password: string;
}

const DB_CREDENTIALS: DbCredentials = {
  host: "localhost",
  port: "5439",
  database: "quantica",
  schema: "quantica_bi",
  user: "metabase_reader",
  password: "metabase_reader_dev_password",
};

export function BiPage() {
  return (
    <div className="flex flex-col gap-6 max-w-[1200px]">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] font-semibold">Business Intelligence</h1>
          <p className="text-[13px] text-muted mt-1">
            Dashboard analitiche su Quantica via Metabase. Dati read-only dallo
            schema{" "}
            <code className="font-mono text-[12px]">quantica_bi</code>.
          </p>
        </div>
        <a
          href={METABASE_URL}
          target="_blank"
          rel="noreferrer"
          className="btn-primary"
        >
          Apri Metabase <span aria-hidden>↗</span>
        </a>
      </header>

      <SetupGuide />
      <DashboardsGrid />
      <DbCredentialsCard />
    </div>
  );
}

function SetupGuide() {
  return (
    <section className="card">
      <h2 className="text-[15px] font-semibold mb-3">
        Setup iniziale (solo al primo avvio)
      </h2>
      <ol className="text-[13px] text-text-2 space-y-3 list-decimal pl-5">
        <li>
          Apri Metabase:{" "}
          <a
            href={METABASE_URL}
            target="_blank"
            rel="noreferrer"
            className="text-accent hover:underline"
          >
            {METABASE_URL}
          </a>
        </li>
        <li>
          Al primo accesso ti chiede di creare un account admin Metabase.
          Inserisci email + password.
        </li>
        <li>
          Nel wizard &ldquo;Add database&rdquo;, scegli{" "}
          <strong>PostgreSQL</strong> e usa le credenziali read-only riportate
          sotto.
        </li>
        <li>
          Una volta connesso, esplora lo schema{" "}
          <code className="font-mono">quantica_bi</code> — contiene 8 views
          pre-configurate per le dashboard.
        </li>
        <li>
          Vai a <strong>+ New → Dashboard</strong> per creare le 6 dashboard
          suggerite (vedi sezione sotto).
        </li>
      </ol>
      <div className="mt-4 text-[11.5px] text-muted">
        Vedi{" "}
        <code className="font-mono">docs/runbooks/metabase-setup.md</code> per
        istruzioni dettagliate + queries SQL pre-fatte.
      </div>
    </section>
  );
}

function DashboardsGrid() {
  return (
    <section>
      <h2 className="text-[15px] font-semibold mb-3">Dashboard suggerite</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {DASHBOARDS.map((d) => (
          <div key={d.title} className="card">
            <div className="text-[14px] font-semibold mb-1">{d.title}</div>
            <p className="text-[12.5px] text-text-2 mb-2">{d.description}</p>
            <code className="text-[11px] font-mono text-muted">{d.view}</code>
          </div>
        ))}
      </div>
    </section>
  );
}

function DbCredentialsCard() {
  const [copied, setCopied] = useState<string | null>(null);

  function copy(key: string, value: string) {
    if (!navigator?.clipboard) return;
    navigator.clipboard.writeText(value).then(
      () => {
        setCopied(key);
        setTimeout(() => setCopied(null), 1500);
      },
      () => {
        // best-effort: niente toast qui per non aggiungere dipendenze. La
        // mancata copia è già evidente perché lo stato `copied` non cambia.
      },
    );
  }

  const rows: Array<[string, string]> = [
    ["Host", DB_CREDENTIALS.host],
    ["Port", DB_CREDENTIALS.port],
    ["Database", DB_CREDENTIALS.database],
    ["Schema", DB_CREDENTIALS.schema],
    ["User", DB_CREDENTIALS.user],
    ["Password", DB_CREDENTIALS.password],
  ];

  return (
    <section className="card">
      <h2 className="text-[15px] font-semibold mb-3">
        Credenziali database (read-only)
      </h2>
      <p className="text-[12.5px] text-muted mb-3">
        Solo dev. In produzione le credenziali verranno gestite via secrets
        manager (HashiCorp Vault / KMS).
      </p>
      <table className="w-full text-[12.5px]">
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} className="border-b border-line">
              <td className="py-2 text-muted w-[120px]">{k}</td>
              <td className="py-2 font-mono">{v}</td>
              <td className="py-2 text-right">
                <button
                  type="button"
                  onClick={() => copy(k, v)}
                  className="text-[11px] text-accent hover:underline"
                >
                  {copied === k ? "Copiato!" : "Copia"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
